import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Heart, MapPin, Phone, MessageSquare, MessageCircle, ShieldCheck, Clock, Eye, ChevronLeft, Loader2, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AuthModal } from "@/components/AuthModal";
import { SellerReviews } from "@/components/SellerReviews";
import { SellerActions } from "@/components/SellerActions";
import { ListingGallery } from "@/components/listing/ListingGallery";
import { ShareMenu } from "@/components/listing/ShareMenu";
import { useListingJsonLd } from "@/components/listing/useListingJsonLd";
import { useSeo } from "@/hooks/useSeo";
import { useAuth } from "@/context/AuthContext";
import api, { formatPrice, timeAgo, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ItemView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [saved, setSaved] = useState(false);
  const [savedReady, setSavedReady] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [showPhone, setShowPhone] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportBusy, setReportBusy] = useState(false);

  const isJobs = listing?.category === "Jobs";
  const isMotors = listing?.category === "Motors";

  useEffect(() => {
    setNotFound(false);
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => setNotFound(true));
    if (user) {
      setSavedReady(false);
      api.get("/favorites/ids")
        .then((r) => setSaved(r.data.includes(id)))
        .catch((e) => console.error("Failed to load favourites", e))
        .finally(() => setSavedReady(true));
    } else {
      setSaved(false);
      setSavedReady(true);
    }
  }, [id, user, navigate]);

  const gone = !!listing && ["sold", "removed"].includes(listing.status);
  const unavailable = notFound || gone;

  useListingJsonLd(gone ? null : listing);

  useSeo({
    title: unavailable
      ? "Listing no longer available | DealShed"
      : listing ? `${listing.title}, ${formatPrice(listing.price, listing.category)} | DealShed` : undefined,
    description: unavailable
      ? "This ad is no longer available on DealShed. Browse similar listings across Ireland instead."
      : listing ? (listing.description || `${listing.title} for sale on DealShed`).slice(0, 155) : undefined,
    canonical: `${window.location.origin}/listing/${id}`,
    image: unavailable ? undefined : `${window.location.origin}/api/og-image/listing/${id}`,
    type: unavailable ? "website" : "product",
    statusCode: notFound ? 404 : gone ? 410 : undefined,
    noindex: unavailable,
  });

  const toggleSave = async () => {
    if (!user) { setAuthOpen(true); return; }
    const { data } = await api.post(`/favorites/${id}`);
    setSaved(data.favorited);
    toast.success(data.favorited ? "Saved to favourites" : "Removed from favourites");
  };

  const sendMessage = async () => {
    if (!user) { setMsgOpen(false); setAuthOpen(true); return; }
    try {
      await api.post("/messages", { listing_id: id, text: msgText });
      toast.success(isJobs ? "Application sent to the employer" : "Message sent to seller");
      setMsgOpen(false); setMsgText("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const submitReport = async () => {
    if (!reportReason) { toast.error("Please choose a reason"); return; }
    setReportBusy(true);
    try {
      await api.post(`/listings/${id}/report`, { reason: reportReason, details: reportDetails });
      toast.success("Thanks, we'll take a look at this ad");
      setReportOpen(false); setReportReason(""); setReportDetails("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setReportBusy(false); }
  };

  const shareUrl = `${window.location.origin}/api/share/listing/${id}`;
  const shareText = listing ? `Check out "${listing.title}" on DealShed` : "Check out this listing on DealShed";

  if (notFound) return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <main className="flex-1 grid place-items-center px-4 py-20 text-center" data-testid="listing-not-found">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">This ad is no longer available</h1>
          <p className="text-slate-500 mb-6 max-w-md mx-auto">It may have been sold or removed. Have a browse for something similar instead.</p>
          <Button onClick={() => navigate("/search")} data-testid="not-found-browse" className="bg-emerald-600 hover:bg-emerald-700 text-white">Browse all ads</Button>
        </div>
      </main>
      <Footer />
    </div>
  );

  if (!listing) return <div className="min-h-screen"><Header /></div>;
  const images = listing.images && listing.images.length ? listing.images : ["https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80"];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full flex-1">
        <button onClick={() => navigate(-1)} data-testid="back-button" className="text-sm text-slate-500 hover:text-slate-900 mb-4 inline-flex items-center gap-1"><ChevronLeft size={16} /> Back to results</button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Gallery + details */}
          <div className="order-2 lg:order-1 lg:col-span-2 space-y-6">
            <ListingGallery images={images} title={listing.title} active={active} setActive={setActive} lightboxOpen={lightboxOpen} setLightboxOpen={setLightboxOpen} />

            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">Description</h2>
              <p className="text-slate-700 whitespace-pre-line">{listing.description}</p>
            </div>

            {listing.specs && Object.keys(listing.specs).length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="specs-table">
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Specifications</h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {Object.entries(listing.specs).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 pb-2">
                      <dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-900">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 flex gap-3" data-testid="safety-advice">
              <ShieldCheck className="text-amber-600 shrink-0" />
              <div>
                <p className="font-semibold text-slate-900">Stay safe when you buy</p>
                <ul className="text-sm text-slate-600 mt-1 list-disc pl-4 space-y-0.5">
                  <li>Meet up somewhere public and bring a friend if you can.</li>
                  <li>Never send money upfront or hand over your bank details.</li>
                  <li>Have a good look at the item before you pay.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sticky sidebar */}
          <div className="order-1 lg:order-2 lg:col-span-1">
            <div className="lg:sticky lg:top-20 space-y-4">
              <div className="bg-white border border-slate-200 rounded-lg p-6">
                <p className="font-heading text-3xl font-extrabold text-emerald-700" data-testid="item-price">{formatPrice(listing.price, listing.category)}</p>
                <h1 className="text-lg font-medium text-slate-900 mt-1" data-testid="item-title">{listing.title}</h1>
                <div className="flex items-center gap-4 text-sm text-slate-500 mt-3">
                  <span className="inline-flex items-center gap-1" data-testid="item-location"><MapPin size={14} /> {[listing.area, listing.county].filter(Boolean).join(", ")}{listing.eircode ? ` · ${listing.eircode}` : ""}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={14} /> {timeAgo(listing.created_at)}</span>
                  <span className="inline-flex items-center gap-1"><Eye size={14} /> {listing.views}</span>
                </div>

                <div className="mt-5 space-y-2">
                  {listing.contact_option === "show_phone" && (
                    showPhone ? (
                      <a href={`tel:${listing.phone}`} className="flex items-center justify-center gap-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-md py-2.5 font-medium" data-testid="phone-revealed"><Phone size={16} /> {listing.phone}</a>
                    ) : (
                      <Button onClick={() => setShowPhone(true)} data-testid="show-phone-button" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"><Phone size={16} className="mr-2" /> Show phone number</Button>
                    )
                  )}
                  {listing.contact_option !== "hide" && (
                    <Button variant={listing.contact_option === "show_phone" ? "outline" : "default"} onClick={() => user ? setMsgOpen(true) : setAuthOpen(true)} data-testid="message-seller-button" className={listing.contact_option === "show_phone" ? "w-full" : "w-full bg-emerald-600 hover:bg-emerald-700 text-white"}><MessageSquare size={16} className="mr-2" /> {isJobs ? "Apply for this job" : "Message seller"}</Button>
                  )}
                  {listing.contact_option === "hide" && <p className="text-sm text-slate-400 text-center">Contact details hidden by seller</p>}
                  <Button variant="ghost" onClick={toggleSave} disabled={user && !savedReady} data-testid="save-button" className={`w-full ${saved ? "text-red-600" : "text-slate-600"}`}>
                    {user && !savedReady
                      ? <><Loader2 size={16} className="mr-2 animate-spin" /> Save ad</>
                      : <><Heart size={16} className="mr-2" fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Save ad"}</>}
                  </Button>
                  <ShareMenu shareUrl={shareUrl} shareText={shareText} />
                  {isMotors && (
                    <Button variant="outline" data-testid="ask-pete-button"
                      onClick={() => navigate(`/?ask_pete=${encodeURIComponent(`Is the ${listing.title} a good buy, and what should I check?`)}`)}
                      className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                      <MessageCircle size={16} className="mr-2" /> Ask Pete about this car
                    </Button>
                  )}
                  <button onClick={() => setReportOpen(true)} data-testid="report-button" className="w-full text-xs text-slate-400 hover:text-slate-600 mt-1 inline-flex items-center justify-center gap-1"><Flag size={12} /> Report this ad</button>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="seller-snippet">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-3">Seller</p>
                <div className="flex items-center gap-3">
                  <Avatar><AvatarFallback className="bg-emerald-100 text-emerald-700">{(listing.seller_name || "S")[0]}</AvatarFallback></Avatar>
                  <div>
                    <p className="font-medium text-slate-900">{listing.seller_name || "DealShed Seller"}</p>
                    <p className="text-xs text-slate-500">Member since {listing.seller_joined ? new Date(listing.seller_joined).getFullYear() : "2025"}</p>
                  </div>
                </div>
                <SellerActions sellerId={listing.seller_id} />
                <SellerReviews sellerId={listing.seller_id} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent data-testid="message-modal">
          <DialogHeader>
            <DialogTitle>{isJobs ? `Apply for "${listing.title}"` : `Message seller about "${listing.title}"`}</DialogTitle>
            <DialogDescription>{isJobs ? "Your application goes straight to the employer's DealShed inbox." : "Your message goes straight to the seller's DealShed inbox."}</DialogDescription>
          </DialogHeader>
          <Textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} rows={4} placeholder={isJobs ? "Introduce yourself and why you're a good fit. Add a phone or email so they can reach you." : "Hi, is this still available?"} data-testid="message-text" />
          <Button onClick={sendMessage} disabled={!msgText.trim()} data-testid="send-message-button" className="bg-emerald-600 hover:bg-emerald-700 text-white">{isJobs ? "Send application" : "Send message"}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-modal">
          <DialogHeader>
            <DialogTitle>Report this ad</DialogTitle>
            <DialogDescription>Tell us what's wrong and our team will review it.</DialogDescription>
          </DialogHeader>
          <Select value={reportReason} onValueChange={setReportReason}>
            <SelectTrigger data-testid="report-reason"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Spam or scam">Spam or scam</SelectItem>
              <SelectItem value="Prohibited or illegal item">Prohibited or illegal item</SelectItem>
              <SelectItem value="In the wrong category">In the wrong category</SelectItem>
              <SelectItem value="Offensive content">Offensive content</SelectItem>
              <SelectItem value="Something else">Something else</SelectItem>
            </SelectContent>
          </Select>
          <Textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} rows={3} placeholder="Add any details (optional)" data-testid="report-details" />
          <Button onClick={submitReport} disabled={reportBusy || !reportReason} data-testid="submit-report-button" className="bg-emerald-600 hover:bg-emerald-700 text-white">{reportBusy ? "Sending..." : "Submit report"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
